import {isNullOrUndefined} from 'util';
import {FormConfig} from '../models/form-config';
import {FieldConfig} from '../models/field-config';
import {FieldType} from '../models/field-type';
import {Injectable} from '@angular/core';
import {Validators} from '@angular/forms';
import {ValidateLandMortgageRegister} from '../validators/land-mortgage-register.validator';
import {RequiredNotBlank} from '../validators/required-not-blank.validator';
import {NIPValidator} from '../validators/nip.validator';
import {PeselValidator} from '../validators/pesel.validator';
import {RegonValidator} from '../validators/regon.validator';
import {MinLengthNotBlank} from '../validators/min-length-not-blank.validator';
import {AND, OR} from '../validators/condition-validators';

import * as _ from 'lodash';
import * as moment from 'moment';
import {PeselLegalAgeValidator} from '../validators/pesel-legal-age.validator';
import {PostalCodeValidator} from '../validators/postal-code.validator';
import {ValidateIban} from '../validators/iban.validator';
import {PhoneNumberZeroesValidator} from '../validators/zeroes.validator';
import {Validator} from '../models/validator.enum';
import {MaterialDatepickerType} from '../enums/material-datepicker-type.enum';
import {startOfYear} from '../utils/time-utils';
import {PhoneNumberRequiredValidator} from '../validators/phone-number-required.validator';

@Injectable()
export class FormService {

  generateArray(formConfig: FormConfig, bean?: any) {
    const array = [];

    formConfig.fieldsConfig.forEach(field => {
      array.push({
        key: field.fieldKey,
        value: bean ? this.getValue(field, bean, formConfig) : '',
        config: this.getConfigForField(field.fieldKey, formConfig)
      });
    });
    return array;
  }

  getValue(field: FieldConfig, bean: any, formConfig: FormConfig) {
    let value = bean;
    if (field.fieldsDataFormat === 'objectArray') {
      if (field.beanConfig && field.beanConfig.propertyName) {
        bean = bean[field.beanConfig.propertyName];
      }
      bean.forEach(o => {
        if (field.beanConfig && field.beanConfig.valuePropertyName) {
          const key = field.beanConfig.keyPropertyValue ? field.beanConfig.keyPropertyValue : field.fieldKey;

          if (key === o[field.beanConfig.keyPropertyName]) {
            value = this.resolveValueFromObject(o, field.beanConfig.valuePropertyName);
          }
        }
      })
    } else if (field.fieldsDataFormat === 'array') {
      let fieldValues = bean[field.fieldKey];
      if (fieldValues) {
        value = fieldValues[0];
      }
    } else {
      let keys = field.fieldKey.split('.');

      keys.forEach(key => {
        value = value[key];
      });

      if (!isNullOrUndefined(value) && field.convertFormat) {
        value = this.convertValue(value, field.convertFormat);
      }
    }

    return value;
  }

  getConfigForField(key: string, formConfig: FormConfig): FieldConfig {
    let fieldConfig;
    if (formConfig !== undefined) {
      formConfig.fieldsConfig.forEach(config => {
        if (config.fieldKey === key) {
          fieldConfig = config;
        }
      });
    }
    return fieldConfig;
  }

  getFieldType(key: string, formConfig: FormConfig) {
    const fieldConfig = this.getConfigForField(key, formConfig);
    if (fieldConfig !== undefined) {
      return fieldConfig.type;
    } else {
      return undefined;
    }
  }

  convertDateToLong(value, formConfig: FormConfig): any {
    formConfig.fieldsConfig.forEach(field => {
      if (FieldType.DateField === field.type) {
        const date = value[field.fieldKey];
        if (date !== undefined && date instanceof Date) {
          value[field.fieldKey] = date.getTime();
        }
      }
    });
    return value;
  }

  convertValuesToDate(value, formConfig: FormConfig): any {
    formConfig.fieldsConfig.forEach(field => {
      if (FieldType.DateField === field.type) {
        const date = value[field.fieldKey];
        if (date !== undefined) {
          value[field.fieldKey] = new Date(date);
        } else {
          value[field.fieldKey] = ''
        }
      }
    });
    return value;
  }

  getLong(value) {
    if (value !== undefined && value instanceof Date) {
      return value.getTime();
    } else {
      return value;
    }
  }

  getDate(value: any, fieldConfig: FieldConfig) {
    const { dateFormat } = fieldConfig;
    if (value !== undefined && value !== null && value !== '') {
      if (fieldConfig.materialDateFieldConfig && fieldConfig.materialDateFieldConfig.type === MaterialDatepickerType.YEAR) {
        return startOfYear(value);
      } else {
        let formatedDate = moment(value).toDate();

        if (formatedDate) {
          return formatedDate;
        }
        return moment(new Date(value)).format(dateFormat);
      }
    } else {
      return value;
    }
  }

  getValidators(validatorsCodes: Array<string>, config?: FieldConfig): any[] {
    let validators = [];
    if (validatorsCodes === undefined) {
      return validators;
    }
    if (_.find(validatorsCodes, el => Validator.REQUIRED === el)) {
      validators.push(Validators.required)
    }
    if (_.find(validatorsCodes, el => Validator.REQUIRED_NOT_BLANK === el)) {
      validators.push(RequiredNotBlank())
    }
    if (_.find(validatorsCodes, el => Validator.REQUIRED_TRUE === el)) {
      validators.push(Validators.requiredTrue)
    }
    if (_.find(validatorsCodes, el => Validator.EMAIL === el)) {
      validators.push(Validators.email)
    }
    if (_.find(validatorsCodes, el => Validator.PHONE_NUMBER_ZEROES === el)) {
      validators.push(PhoneNumberZeroesValidator)
    }
    if (_.find(validatorsCodes, el => Validator.PHONE_NUMBER_REQUIRED === el)) {
      validators.push(PhoneNumberRequiredValidator)
    }
    if (_.find(validatorsCodes, el => Validator.NIP === el)) {
      validators.push(NIPValidator)
    }
    if (_.find(validatorsCodes, el => Validator.PESEL === el)) {
      validators.push(PeselValidator)
    }
    if (_.find(validatorsCodes, el => Validator.LEGAL_AGE_PESEL === el)) {
      validators.push(PeselLegalAgeValidator)
    }
    if (_.find(validatorsCodes, el => Validator.REGON === el)) {
      validators.push(RegonValidator)
    }
    if (_.find(validatorsCodes, el => Validator.LAND_MORTGAGE_REGISTER === el)) {
      validators.push(ValidateLandMortgageRegister)
    }
    if (_.find(validatorsCodes, el => Validator.POSTAL_CODE === el)) {
      validators.push(PostalCodeValidator)
    }
    if (_.find(validatorsCodes, el => Validator.IBAN === el)) {
      if (config) {
        validators.push(ValidateIban(config.properties.matPrefix))
      } else {
        validators.push(ValidateIban())
      }
    }
    const pattern = _.find(validatorsCodes, { name: Validator.PATTERN });
    if (pattern !== undefined) {
      validators.push(Validators.pattern(pattern.pattern));
    }
    const patternEmail = _.find(validatorsCodes, { name: Validator.PATTERN_EMAIL });
    if (patternEmail !== undefined) {
      validators.push(Validators.pattern(patternEmail.patternEmail));
    }
    const maxLength = _.find(validatorsCodes, { name: Validator.MAX_LENGTH });
    if (maxLength !== undefined) {
      validators.push(Validators.maxLength(maxLength.maxLength));
    }
    const minLength = _.find(validatorsCodes, { name: Validator.MIN_LENGTH });
    if (minLength !== undefined) {
      validators.push(Validators.minLength(minLength.minLength));
    }
    const minLengthNotBlank = _.find(validatorsCodes, { name: Validator.MIN_LENGTH_NOT_BLANK });
    if (minLengthNotBlank !== undefined) {
      validators.push(MinLengthNotBlank(minLengthNotBlank.minLengthNotBlank));
    }
    const or = _.find(validatorsCodes, { name: Validator.OR });
    if (or !== undefined) {
      validators.push(OR(this.getValidators(or.validators), or.errorType))
    }
    const and = _.find(validatorsCodes, { name: Validator.AND });
    if (and !== undefined) {
      validators.push(AND(this.getValidators(and.validators), and.errorType))
    }
    return validators;
  }

  private resolveValueFromObject(o: any, path: string): any {
    const propsChain = path.split('.');
    let value = o;
    propsChain.forEach(property => value = value[property]);
    return value;
  }

  private convertValue(value: any, format: string): any {
    let convertedValue = value;
    switch (format) {
      case 'string':
        convertedValue += '';
        break;
      default:
        break;
    }
    return convertedValue;
  }
}
